import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';
import { parseIntOrDefault } from '@theme/utilities';

/**
 * A custom element that wraps a native `<select>` for cart-line quantity
 * changes. Used on the cart page to match the GANT design (a single hollow
 * outline cell with a chevron) while preserving the AJAX cart flow that
 * `cart-items-component` already drives via `QuantitySelectorUpdateEvent`.
 *
 * The first option of the `<select>` represents removal (quantity = 0). When
 * chosen, `cart-items-component` interprets a 0 as a line removal.
 *
 * @typedef {Object} Refs
 * @property {HTMLSelectElement} quantitySelect
 *
 * @extends {Component<Refs>}
 */
class CartLineQuantitySelectComponent extends Component {
  requiredRefs = ['quantitySelect'];

  /**
   * Handles the `<select>` change event. Dispatches the same
   * `QuantitySelectorUpdateEvent` shape that the +/- selector emits, so the
   * parent `cart-items-component` can update the line.
   *
   * @param {Event} event
   */
  onChange(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const { quantitySelect } = this.refs;
    const quantity = parseIntOrDefault(quantitySelect.value, 0);
    const cartLine = parseIntOrDefault(quantitySelect.dataset.cartLine, 0) || undefined;

    this.dispatchEvent(new QuantitySelectorUpdateEvent(quantity, cartLine));
  }
}

if (!customElements.get('cart-line-quantity-select-component')) {
  customElements.define('cart-line-quantity-select-component', CartLineQuantitySelectComponent);
}
